// core/features/templates/api.ts
import { api } from "core/axios/axios";
import {
    IGlobalTemplate,
    IRawUserTemplatesResponse,
    IUserTemplate,
    IUserTemplatesListParams,
    IUserTemplatesListResult,
    ITrashedTemplate,
    IGenerateTemplateInput,
    IGenerateTemplateResponse,
    TemplateGenerationErrorKind,
} from "./types";

/**
 * Fetch global/predefined templates
 * @returns Array of global templates with S3 content URLs and CloudFront thumbnails
 */
export async function fetchGlobalTemplates(): Promise<IGlobalTemplate[]> {
    console.log("🌐 fetchGlobalTemplates called");
    const { data } = await api.get<IGlobalTemplate[]>("templates-global");
    console.log("📦 Global templates response:", data, "isArray:", Array.isArray(data));
    // API returns an array directly
    const result = Array.isArray(data) ? data : [];
    console.log("✅ Returning", result.length, "global templates");
    return result;
}

/**
 * Normalize user templates from API response
 * The API returns an indexed object like { "1": {...}, "2": {...} }
 * This function converts it to a proper array
 * 
 * @param input - Can be an indexed object or array from All_templates field
 * @returns Array of user templates
 */
function normalizeUserTemplates(
    input: IRawUserTemplatesResponse["All_templates"]
): IUserTemplate[] {
    // Already an array
    if (Array.isArray(input)) return input;

    // Indexed object like { "1": {...}, "2": {...} }
    if (input && typeof input === "object") {
        return Object.values(input);
    }

    // Fallback for null/undefined
    return [];
}

/**
 * Fetch user-created templates
 * @param params - Query parameters, paginator=0 returns all templates
 * @returns Normalized list of user templates with metadata
 * 
 * Response format:
 * {
 *   All_templates: {
 *     "1": { id: 53, name: "...", tag: "...", temp_used_in_campaign: [...], ... },
 *     "2": { id: 54, ... },
 *     ...
 *   },
 *   Total_templates: 9
 * }
 */
export async function fetchUserTemplates(
    params: IUserTemplatesListParams = { paginator: 0 }
): Promise<IUserTemplatesListResult> {
    const paginator = params?.paginator ?? 0;
    console.log("📥 fetchUserTemplates called with paginator:", paginator);
    const { data } = await api.get<IRawUserTemplatesResponse>(
        `templates?paginator=${encodeURIComponent(String(paginator))}`
    );

    console.log("📦 Raw API response:", data);
    console.log("📊 All_templates type:", typeof data.All_templates, "isArray:", Array.isArray(data.All_templates));

    const normalizedItems = normalizeUserTemplates(data.All_templates);
    console.log("✅ Normalized items count:", normalizedItems.length, "items:", normalizedItems);

    const result = {
        items: normalizedItems,
        total: data.Total_templates ?? 0,
        paginator,
    };

    console.log("🎯 Returning result:", result);
    return result;
}



// *********** EDITOR FUNCTIONS - STARTED FROM HERE ***********

/**
 * Save a new campaign template
 * @param body - Template data including content, name, etc.
 */
export async function saveCampaignTemplateRes(body: any) {
    return api.post("campaign-storage", body);
}

/**
 * Update an existing campaign template
 * @param body - Must include template_id and updated fields
 */
export async function updateCampaignTemplateRes(body: any) {
    return api.put("campaign-storage/" + body.template_id, body);
}

/**
 * Get pre-signed URL for uploading template to S3
 * @param name - Template name
 * @param thumb_url - Thumbnail URL
 */
export async function getPreAssignedUrlRes(name: string, thumb_url: any) {
    return api.get("storagefacility?template_name=" + name + "&thumb_url=" + thumb_url);
}

/**
 * Get pre-signed URL for updating template thumbnail
 * @param id - Template ID
 * @param thumb_url - New thumbnail URL
 */
export async function gePutPreAssignedUrlRes(id: any, thumb_url: any) {
    return api.get(
        "storagefacility/updatetemplate?template_id=" +
        id +
        "&thumb_url=" +
        thumb_url,
    );
}

/**
 * Fetch template content from S3 URL
 * @param url - Pre-signed S3 URL
 */
export async function getTemplateFromUrl(url: string) {
    try {
        const template = await api.get(url);
        return template;
    } catch (error) {
        throw error;
    }
}

/**
 * Get campaign template storage details
 * @param bucketId - Template bucket/storage ID
 */
export async function getCampaignTemplateRes(bucketId: any) {
    return api.get("campaign-storage?template_id=" + bucketId);
}

/**
 * Upload document to S3 using pre-signed URL
 * @param url - Pre-signed S3 upload URL
 * @param formData - Form data with file
 */
export async function uploadCustomDocumentRes(url: string, formData: FormData) {
    const config = {
        headers: {
            "Content-Type": "application/xml",
        },
    };
    return api.post(url, formData, config);
}

/**
 * Save test template (for A/B testing)
 * @param body - Test template data
 */
export async function saveTestTemplateRes(body: any) {
    return api.post("testTemplate", body);
}

export async function createByOpenApi(
    body: IGenerateTemplateInput & { request_id: string },
    timeout = GENERATE_TIMEOUT_MS,
) {
    return api.post<IGenerateTemplateResponse>("genai/openai", body, { timeout });
}

const GENERATE_TIMEOUT_MS = 120_000;
/** How long one user action keeps trying, including waiting out a generation still running server-side. */
const GENERATE_DEADLINE_MS = 180_000;
/** Retries for failures other than "still in progress": network drops, 5xx, spent ids. */
const GENERATE_MAX_RETRIES = 3;
const GENERATE_BACKOFF_MS = [1_000, 2_000, 4_000];
/** Poll interval while the server reports the same request_id as still in progress. */
const GENERATE_PENDING_POLL_MS = 3_000;

export class TemplateGenerationError extends Error {
    constructor(
        message: string,
        public kind: TemplateGenerationErrorKind,
        public status?: number,
        /** Set for "in_progress": pass it back to `generateTemplate` to pick up that generation. */
        public requestId?: string,
    ) {
        super(message);
        this.name = "TemplateGenerationError";
    }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Reads `code` and `message` from an error body, flat or nested under `error`
 * (`{ error: { code, message } }`). Only strings are taken, so an unexpected
 * shape yields "" rather than an object that breaks string handling or rendering.
 */
function readErrorBody(data: any): { code: string; message: string } {
    if (typeof data === "string") return { code: "", message: data };
    const nested = data?.error && typeof data.error === "object" ? data.error : undefined;
    const pick = (...values: unknown[]) =>
        (values.find((v) => typeof v === "string" && v.trim()) as string | undefined) ?? "";
    return {
        code: pick(data?.code, data?.error_code, nested?.code, nested?.error_code).toUpperCase(),
        message: pick(data?.message, nested?.message, data?.error, data?.detail),
    };
}

/** What to do after an error response. */
type GenerateOutcome =
    /** Still running server-side: poll with the same id, bounded by the deadline. */
    | { action: "poll" }
    /** Retry, with a new id only if the old one is spent. */
    | { action: "retry"; newId: boolean; mayBeRunning: boolean }
    | { action: "throw"; kind: TemplateGenerationErrorKind; fallback: string };

const POLL: GenerateOutcome = { action: "poll" };
const NEW_ID: GenerateOutcome = { action: "retry", newId: true, mayBeRunning: false };
/** Same id; the server never started work on it. */
const SAME_ID_NOT_STARTED: GenerateOutcome = { action: "retry", newId: false, mayBeRunning: false };
/** Same id; the server may have started (gateway errors). */
const SAME_ID_MAYBE_RUNNING: GenerateOutcome = { action: "retry", newId: false, mayBeRunning: true };

const fail = (kind: TemplateGenerationErrorKind, fallback: string): GenerateOutcome => ({
    action: "throw", kind, fallback,
});

/**
 * Machine-readable codes the backend may send as `code` (or `error_code`).
 * Names to be confirmed with the backend. Each applies only on the status the
 * contract gives it, so a stray code on another status (e.g. a 5xx) cannot,
 * say, mint a new id while the original may still be charging.
 */
const OUTCOME_BY_CODE: Record<string, { status: number; outcome: GenerateOutcome }> = {
    GENERATION_IN_PROGRESS: { status: 409, outcome: POLL },
    REQUEST_ID_REUSED: { status: 409, outcome: NEW_ID },
    REQUEST_ID_CONSUMED: { status: 409, outcome: NEW_ID },
    RESULT_EXPIRED: { status: 410, outcome: NEW_ID },
    GENERATION_FAILED: { status: 503, outcome: NEW_ID },
    CREDITS_NOT_RESERVED: { status: 503, outcome: SAME_ID_NOT_STARTED },
};

/**
 * The single classifier for genai/openai error responses: the error code when
 * it is known and matches the status, otherwise the status, with the message
 * text only to tell apart the cases the contract shares a status between.
 */
function classifyGenerateError(status: number, { code, message }: { code: string; message: string }): GenerateOutcome {
    const byCode = OUTCOME_BY_CODE[code];
    if (byCode && byCode.status === status) return byCode.outcome;

    const msg = message.toLowerCase().replace(/[_-]/g, " ");
    switch (status) {
        case 400:
            return fail("invalid", "Please provide a valid prompt.");
        case 403:
            // API Gateway's authorizer denies with this wording; it is a session
            // problem, not a missing plan.
            return /not authorized to access|explicit deny/.test(msg)
                ? fail("auth", "Your session could not be verified. Please sign in again.")
                : fail("entitlement", "Your plan doesn't include AI template generation. Upgrade to continue.");
        case 409:
            return msg.includes("in progress") ? POLL : NEW_ID;
        case 410:
            return NEW_ID;
        case 413:
            return fail("too_large", "Your prompt or template is too large. Please shorten it and try again.");
        case 429:
            // Gateway throttling never reached the generator: retry the same id.
            return /too many requests|rate exceeded|throttl/.test(msg)
                ? SAME_ID_NOT_STARTED
                : fail("quota", "You've used all your template credits for this billing period.");
        case 503:
            return /new request id/.test(msg) ? NEW_ID : SAME_ID_NOT_STARTED;
    }
    if (status >= 500) return SAME_ID_MAYBE_RUNNING;
    return fail("failed", "Template generation failed. Please try again.");
}

/**
 * Generate (or refine) a template via genai/openai, following the request_id
 * idempotency contract. Call once per user action.
 *
 * - Ambiguous failures (timeout, network drop, gateway 5xx), 503 "could not
 *   be reserved" and gateway throttling (429 "Too Many Requests") retry with the
 *   SAME request_id, so the user is never charged twice.
 * - 409 "still in progress" is polled with the same request_id until the deadline.
 * - A new request_id is minted only when the server says the old one is spent
 *   (503 "retry with a new request_id", 409 already used / new attempt, 410 expired).
 * - 400, 403 (entitlement or auth), 413 and 429 quota are thrown immediately
 *   as TemplateGenerationError. See `classifyGenerateError`.
 *
 * If the deadline passes while the server may still be generating, this throws
 * kind "in_progress" carrying the request_id. Retrying the same input with
 * `{ requestId }` then returns that generation instead of charging for a new one.
 */
export async function generateTemplate(
    input: IGenerateTemplateInput,
    options: { requestId?: string } = {},
): Promise<IGenerateTemplateResponse> {
    const body: IGenerateTemplateInput = { prompt: input.prompt };
    if (input.session_id) body.session_id = input.session_id;
    if (input.template_id != null && input.template_id !== "") body.template_id = input.template_id;
    if (input.html) body.html = input.html;

    let requestId = options.requestId ?? crypto.randomUUID();
    let lastMessage = "Template generation failed. Please try again.";
    let lastStatus: number | undefined;
    /** The server may still be generating `requestId`. */
    let mayBeRunning = false;
    let retries = 0;
    let wait = 0;
    const deadline = Date.now() + GENERATE_DEADLINE_MS;

    const retry = () => {
        retries += 1;
        wait = GENERATE_BACKOFF_MS[retries - 1] ?? GENERATE_BACKOFF_MS[GENERATE_BACKOFF_MS.length - 1];
        return retries <= GENERATE_MAX_RETRIES;
    };

    while (true) {
        if (wait) await sleep(wait);
        const remaining = deadline - Date.now();
        if (remaining <= 0) break;

        try {
            const { data } = await createByOpenApi(
                { ...body, request_id: requestId },
                Math.min(GENERATE_TIMEOUT_MS, remaining),
            );
            return data;
        } catch (e: any) {
            const status: number | undefined = e?.response?.status;
            const error = readErrorBody(e?.response?.data);
            if (error.message) lastMessage = error.message;
            lastStatus = status;

            // No response at all (timeout / network drop): safe idempotent retry.
            if (!status) {
                mayBeRunning = true;
                if (!retry()) break;
                continue;
            }

            const outcome = classifyGenerateError(status, error);

            // The authorizer's own wording means nothing to the user; ours does.
            if (outcome.action === "throw")
                throw new TemplateGenerationError(
                    outcome.kind === "auth" ? outcome.fallback : error.message || outcome.fallback,
                    outcome.kind, status);

            if (outcome.action === "poll") {
                mayBeRunning = true;
                wait = GENERATE_PENDING_POLL_MS;
                continue;
            }

            if (outcome.newId) requestId = crypto.randomUUID();
            mayBeRunning = outcome.mayBeRunning;
            if (!retry()) break;
        }
    }

    if (mayBeRunning)
        throw new TemplateGenerationError(
            "Your template is still being generated. Send the same request again in a moment to pick it up — you won't be charged twice.",
            "in_progress", lastStatus, requestId);
    throw new TemplateGenerationError(lastMessage, "failed", lastStatus);
}

/**
 * Delete a campaign template (soft-delete → moved to trash)
 * @param templateId - ID of template to soft-delete
 */
export async function deleteCampaignTemplate(templateId: string | number) {
    return api.delete(`campaign-storage/${templateId}`);
}

// ─── TRASH ENDPOINTS ───────────────────────────────────────────────────────

/**
 * Soft-delete (trash) an editor template
 * @param templateId - ID of the editor template to soft-delete
 */
export async function deleteEditorTemplate(templateId: string | number) {
    return api.delete(`template-editor-storage/${templateId}`);
}

/**
 * List all trashed templates (both legacy and editor types)
 * GET /template-trash
 *
 * Response shape: { Trashed_templates: { "1": {...}, "2": {...} }, Total_trashed: N }
 */
export async function fetchTrashedTemplates(): Promise<ITrashedTemplate[]> {
    const { data } = await api.get<any>("template-trash");

    // ── Actual backend shape: { Trashed_templates: { "1": {...} }, Total_trashed: N }
    if (data?.Trashed_templates && typeof data.Trashed_templates === "object") {
        return Object.values(data.Trashed_templates) as ITrashedTemplate[];
    }

    // ── Fallback: bare array
    if (Array.isArray(data)) return data;

    // ── Fallback: { items: [...] }
    if (data?.items && Array.isArray(data.items)) return data.items;

    // ── Fallback: { data: [...] }
    if (data?.data && Array.isArray(data.data)) return data.data;

    return [];
}

/**
 * Restore a trashed template back to its original list
 * PUT /template-trash/{Tid}
 */
export async function restoreTrashedTemplate(templateId: string | number) {
    return api.put(`template-trash/${templateId}`, {});
}

/**
 * Permanently delete a template from trash (also cleans up S3)
 * DELETE /template-trash/{Tid}
 */
export async function permanentlyDeleteTemplate(templateId: string | number) {
    return api.delete(`template-trash/${templateId}`);
}
